import { useState, useCallback, useEffect } from 'react';
import { TypingProgress } from '../../../types/Typing';
import { SPACE_MARK } from '../../../constants/Consts';
import { RESULT_TYPE, THEMES } from '../../../constants/Consts';

interface DisplayWordProps {
  themeType: THEMES;
  character: string;
  gradeNumber: number;
  typingProgress: TypingProgress | undefined;
  score: number;
  limitTime: number | undefined;
  resultType: RESULT_TYPE | undefined;
  handleGetStyle: (tailwindStyle: string) => string;
  displayWord: string;
  inputWord: string;
  tutorialDisplayWord: string;
}

interface DisplayTypingTextInfo {
  completedText: string;
  remainingText: string;
}

export const DisplayWord = ({
  themeType,
  character,
  gradeNumber,
  typingProgress,
  score,
  limitTime,
  resultType,
  handleGetStyle,
  displayWord,
  inputWord,
  tutorialDisplayWord,
}: DisplayWordProps) => {
  const [displayTypingTextInfo, setDisplayTypingTextInfo] = useState<DisplayTypingTextInfo | undefined>(undefined);
  const [isLeftIconError, setIsLeftIconError] = useState<boolean>(false);
  const [isRightIconError, setIsRightIconError] = useState<boolean>(false);

  const env = import.meta.env;
  const themeImagePath: string = `/${themeType}/${character}/`;

  const createDisplayText = useCallback((typingProgress: TypingProgress | undefined) => {
    if (!typingProgress) {
      return;
    }

    const currentCharIndex = typingProgress.completedText.length;
    setDisplayTypingTextInfo({
      completedText: typingProgress.wordText.slice(0, currentCharIndex)?.replace(/ /g, SPACE_MARK),
      remainingText: typingProgress.wordText.slice(currentCharIndex)?.replace(/ /g, SPACE_MARK),
    });
  }, []);

  const renderdDisplayTypingText = () => {
    if (!displayTypingTextInfo) return null;

    const textContent = (
      <>
        <span className="text-completeChar tracking-wide">{displayTypingTextInfo.completedText}</span>
        <span className="text-currentChar tracking-wide">{displayTypingTextInfo.remainingText[0]}</span>
        <span className="text-remainingChar tracking-wide">{displayTypingTextInfo.remainingText.slice(1)}</span>
      </>
    );

    const baseClassName = handleGetStyle('text-2xl tb:text-3xl pc:text-4xl tracking-wide');
    const robloxClassName = `${handleGetStyle('text-lg tb:text-xl pc:text-2xl tracking-wide')} font-verdana`;

    return (
      <p className={themeType === THEMES.ROBLOX ? robloxClassName : baseClassName}>
        {textContent}
      </p>
    );
  };

  useEffect(() => {
    createDisplayText(typingProgress);
  }, [createDisplayText, typingProgress]);

  return (
    <div className="relative pt-[3%]">
      <div className="relative w-full flex flex-col items-center justify-center font-bold">
        {gradeNumber && (
          <img className="absolute w-[20%] top-[-3%]" src={`${env.VITE_DIGITANE_CONTENTS_URL}/typing/grades/${gradeNumber}.webp`} alt={'X級'} />
        )}
        <img className="w-[85%] mx-auto aspect-[4/1]" src={`${themeImagePath}word_frame.webp`} alt={'ワードフレーム'} />
        {resultType && (
          <img className="absolute h-[30%] top-[50%] animate-fade-in-short" src={`/common/result_type/${resultType}.webp`} alt={'結果'} />
        )}
        {tutorialDisplayWord && (
            <div className="absolute bottom-[20%] h-[60%] flex flex-col items-center justify-end gap-[2%]">
              <p className={handleGetStyle('text-4xl tb:text-5xl pc:text-6xl')}>
                {tutorialDisplayWord}
              </p>
            </div>
        )}
        {typingProgress && displayTypingTextInfo && (
          <>
            <div className="absolute bottom-[20%] h-[60%] flex flex-col items-center justify-end gap-[2%]">
              {renderdDisplayTypingText()}
              {displayWord === inputWord ? (
                <p className={handleGetStyle('text-lg tb:text-xl pc:text-2xl')}>
                  <span className="text-completeChar">{typingProgress.completedRomaji?.toLocaleUpperCase() || ''}</span>
                  <span className="text-currentChar">{typingProgress.remainingRomaji?.[0]?.toLocaleUpperCase() || ''}</span>
                  <span className="text-remainingChar">{typingProgress.remainingRomaji?.slice(1)?.toLocaleUpperCase() || ''}</span>
                </p>
              ) : (
                <p className={`${handleGetStyle('text-base tb:text-lg pc:text-xl')} text-translation`}>{displayWord ? displayWord : '　'}</p>
              )}
            </div>
            <div className="absolute bottom-[-5%] w-[25%] flex items-center">
              <div className={`absolute w-full flex justify-evenly text-white ${handleGetStyle('text-lg tb:text-xl pc:text-2xl')}`}>
                <div className="flex items-center gap-[10%]">
                  <img className="w-full object-contain" src={`/common/icons/score.webp`} alt={'スコア'} />
                  <p>{score}</p>
                </div>
                <div className="w-[1%] h-initial rounded bg-white" />
                <div className="flex items-center gap-[10%]">
                  <img className="w-full object-contain" src={`/common/icons/clock.webp`} alt={'時計'} />
                  <p>{limitTime}</p>
                </div>
              </div>
              <img src={`/common/score_frame.webp`} alt={'スコアフレーム'} />
            </div>
          </>
        )}
      </div>
      {!isLeftIconError && (
        <img
          className="absolute w-[15%] bottom-0 left-0 object-contain"
          src={`${themeImagePath}left_icon.webp`}
          alt={'左アイコン'}
          onError={() => setIsLeftIconError(true)}
        />
      )}
      {!isRightIconError && (
        <img
          className="absolute w-[15%] bottom-0 right-0 object-contain"
          src={`${themeImagePath}right_icon.webp`}
          alt={'右アイコン'}
          onError={() => setIsRightIconError(true)}
        />
      )}
    </div>
  );
};
